import {
  authUserResponseSchema,
  countrySchema,
  loginResponseSchema,
  refreshResponseSchema,
  AcceptInvitationRequest,
  ChangePasswordRequest,
  LoginRequest,
  SignUpRequest,
  UpdateProfileRequest,
} from '@/features/auth/services/schemas.ts';
import { ApiClient } from '@/common/api/ApiClient.ts';
import { mockSession } from '@/lib/mockSession.ts';
import { setAccessToken } from '@/features/auth/tokenStore.ts';
import { z } from 'zod';

export const AccountService = {
  getCurrentUser: async () => {
    return await ApiClient.get('/accounts/user/', authUserResponseSchema, undefined, {
      mock: () => ({
        firstName: 'Test',
        lastName: 'User',
        email: 'test@test.cz',
      }),
    });
  },

  login: async (data: LoginRequest) => {
    const response = await ApiClient.post('/accounts/user/login', data, loginResponseSchema, {
      unauthenticated: true,
      mock: () => {
        mockSession.setLoggedIn();
        return { accessToken: 'mock-token' };
      },
    });
    setAccessToken(response.accessToken);
  },

  refresh: async () => {
    const response = await ApiClient.post(
      '/accounts/user/login/refresh',
      undefined,
      refreshResponseSchema,
      {
        _isRefreshRequest: true,
      }
    );
    setAccessToken(response.accessToken);
    return response.accessToken;
  },

  register: async (data: SignUpRequest) => {
    await ApiClient.post('/accounts/register/', data, z.object({}), {
      unauthenticated: true,
      mock: () => {
        mockSession.setLoggedIn();
        return {};
      },
    });
  },

  acceptInvitation: async ({ token, ...data }: AcceptInvitationRequest & { token: string }) => {
    await ApiClient.put(`/accounts/user/invite/${token}`, data, z.object({}), {
      unauthenticated: true,
      mock: () => ({}),
    });
  },

  requestPasswordReset: async (data: { email: string }) => {
    await ApiClient.post('/accounts/user/password/reset', data, z.object({}), {
      unauthenticated: true,
      mock: () => ({}),
    });
  },

  updateProfile: async (data: UpdateProfileRequest) => {
    return await ApiClient.patch('/accounts/user/', data, authUserResponseSchema, {
      mock: () => ({
        firstName: data.firstName ?? 'Test',
        lastName: data.lastName ?? 'User',
        email: 'test@test.cz',
        phone: data.phone,
      }),
    });
  },

  changePassword: async (data: ChangePasswordRequest) => {
    await ApiClient.post('/accounts/user/password/change', data, z.object({}), {
      mock: () => ({}),
    });
  },

  logout: async () => {
    await ApiClient.post('/accounts/user/logout', undefined, z.unknown(), {
      mock: () => {
        mockSession.logout();
        return null;
      },
    });
    setAccessToken(null);
  },

  resendVerificationEmail: async (data: { email: string }) => {
    await ApiClient.post('/accounts/register/email/resend', data, z.object({}), {
      unauthenticated: true,
      mock: () => ({}),
    });
  },

  resetPasswordConfirm: async ({ token, password }: { token: string; password: string }) => {
    await ApiClient.post(
      `/accounts/user/password/reset/confirm/${token}/`,
      { password },
      z.object({}),
      { unauthenticated: true, mock: () => ({}) }
    );
  },

  verifyEmail: async ({ uidb64, token }: { uidb64: string; token: string }) => {
    await ApiClient.get(
      `/accounts/register/email/verify/${uidb64}/${token}/`,
      z.unknown(),
      undefined,
      {
        unauthenticated: true,
        mock: () => null,
      }
    );
  },

  getCountries: async () => {
    return ApiClient.get('/accounts/countries', z.array(countrySchema), undefined, {
      mock: () => [
        { code: 'CZ', name: 'Czech Republic' },
        { code: 'SK', name: 'Slovakia' },
        { code: 'DE', name: 'Germany' },
        { code: 'AT', name: 'Austria' },
        { code: 'PL', name: 'Poland' },
        { code: 'HU', name: 'Hungary' },
        { code: 'FR', name: 'France' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'GB', name: 'United Kingdom' },
      ],
    });
  },
};
